<template>
  <div
    class="col-span-6 sm:col-span-3 xl:col-span-2 flex flex-col justify-end items-center"
    v-show="loading"
  >
    <LoadingIcon icon="three-dots" class="w-8 h-8" />
  </div>
  <div v-if="!loading">
    <div class="intro-y flex flex-col sm:flex-row items-center mt-8">
      <FileTextIcon />
      <h2 class="text-lg font-medium mr-auto">
        {{ $t('superadmin.tools.signUp.detail') }}
      </h2>
      <div class="w-full sm:w-auto flex mt-4 sm:mt-0">
        <div
          class="btn btn-success shadow-md flex items-center mx-2"
          @click="showApproveModal"
          v-if="!signUpData.processed"
        >
          <CheckIcon class="w-4 h-4 mr-2" /> {{ $t('common.approve') }}
        </div>
        <div
          class="btn btn-warning shadow-md flex items-center mx-2"
          @click="showRejectModal"
          v-if="!signUpData.processed"
        >
          <XIcon class="w-4 h-4 mr-2" /> {{ $t('common.reject') }}
        </div>
        <router-link
          :to="{ name: 'superadmin-tools-sign-up-list' }"
          tag="a"
          class="btn shadow-md flex items-center"
        >
          <ArrowLeftCircleIcon class="w-4 h-4 mr-2" /> {{ $t('common.back') }}
        </router-link>
      </div>
    </div>
    <div class="intro-y box px-5 pt-5 mt-5">
      <div
        class="flex flex-col lg:flex-row border-b border-gray-200 dark:border-dark-5 pb-5 -mx-5"
      >
        <div
          class="mt-6 lg:mt-0 flex-1 dark:text-gray-300 px-5 border-l border-r border-gray-200 dark:border-dark-5 border-t lg:border-t-0 pt-5 lg:pt-0"
        >
          <div class="font-medium text-center lg:text-left lg:mt-3">
            {{ $t('common.contactData') }}
          </div>
          <div class="flex flex-col justify-center items-center lg:items-start mt-4">
            <div class="truncate sm:whitespace-normal flex items-center mt-3">
              {{ signUpData.name }} {{ signUpData.lastname }}
            </div>
            <div class="truncate sm:whitespace-normal flex items-center mt-3">
              <MailIcon class="w-4 h-4 mr-2" />
              {{ signUpData.email }}
            </div>
            <div class="truncate sm:whitespace-normal flex items-center mt-3">
              <PhoneIcon class="w-4 h-4 mr-2" />
              {{ signUpData.phoneNumber }}
            </div>
            <div class="truncate sm:whitespace-normal flex items-center mt-3">
              <CloudIcon class="w-4 h-4 mr-2" />
              {{ signUpData.web }}
            </div>
            <div class="truncate sm:whitespace-normal flex items-center mt-3">
              <DollarSignIcon class="w-4 h-4 mr-2" />
              {{ signUpData.iban }}
            </div>
          </div>
        </div>
        <div
          class="mt-6 lg:mt-0 flex-1 dark:text-gray-300 px-5 border-l border-r border-gray-200 dark:border-dark-5 border-t lg:border-t-0 pt-5 lg:pt-0"
        >
          <div class="font-medium text-center lg:text-left lg:mt-3">
            {{ $t('common.companyData') }}
          </div>
          <div class="flex flex-col justify-center items-center lg:items-start mt-4">
            <div class="truncate sm:whitespace-normal flex items-center mt-3">
              {{ signUpData.businessName }}
            </div>
            <div class="truncate sm:whitespace-normal flex items-center mt-3">
              {{ signUpData.businessId }}
            </div>
            <div class="truncate sm:whitespace-normal flex items-center mt-3">
              {{ signUpData.taxId }}
            </div>
            <div class="truncate sm:whitespace-normal flex items-center mt-3">
              {{ signUpData.vatRegistrationId }}
            </div>
          </div>
        </div>
        <div
          class="mt-6 lg:mt-0 flex-1 dark:text-gray-300 px-5 border-l border-r border-gray-200 dark:border-dark-5 border-t lg:border-t-0 pt-5 lg:pt-0"
        >
          <div class="font-medium text-center lg:text-left lg:mt-3">
            {{ $t('address.label') }}
          </div>
          <div class="flex flex-col justify-center items-center lg:items-start mt-4">
            <div class="truncate sm:whitespace-normal flex items-center mt-3">
              {{ signUpData.street }} {{ signUpData.streetNumber }}
            </div>
            <div class="truncate sm:whitespace-normal flex items-center mt-3">
              {{ signUpData.city }}
            </div>
            <div class="truncate sm:whitespace-normal flex items-center mt-3">
              {{ signUpData.zip }}
            </div>
          </div>
        </div>
        <div
          class="mt-6 lg:mt-0 flex-1 dark:text-gray-300 px-5 border-l border-r border-gray-200 dark:border-dark-5 border-t lg:border-t-0 pt-5 lg:pt-0"
        >
          <div class="font-medium text-center lg:text-left lg:mt-3">
            {{ $t('common.detail') }}
          </div>
          <div class="flex flex-col justify-center items-center lg:items-start mt-4">
            <div class="truncate sm:whitespace-normal flex items-center mt-3">
              {{ $t('common.created') }} - {{ formatDate(signUpData.created) }}
            </div>
            <div class="truncate sm:whitespace-normal flex items-center mt-3">
              {{ $t('common.processed') }} -
              {{ signUpData.processed ? this.$t('common.yes') : this.$t('common.no') }}
              <span v-if="signUpData.processed">
                -
                {{
                  signUpData.deleted === null
                    ? this.$t('common.approved')
                    : this.$t('common.rejected')
                }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- BEGIN: Failed Notification Content -->
  <div id="failed-load-notification-content" class="toastify-content hidden flex">
    <XCircleIcon class="text-theme-6" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('superadmin.tools.signUp.notLoaded') }}!</div>
      <!-- <div class="text-gray-600 mt-1">Please check the fileld form.</div> -->
    </div>
  </div>
  <!-- END: Failed Notification Content -->
  <!-- BEGIN: Success Notification Content -->
  <div id="success-reject-notification-content" class="toastify-content hidden flex">
    <CheckCircleIcon class="text-theme-9" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('superadmin.tools.signUp.rejected') }}!</div>
      <!-- <div class="text-gray-600 mt-1">
        Please check your e-mail for further info!
      </div> -->
    </div>
  </div>
  <!-- END: Success Notification Content -->
  <!-- BEGIN: Failed Notification Content -->
  <div id="failed-reject-notification-content" class="toastify-content hidden flex">
    <XCircleIcon class="text-theme-6" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('superadmin.tools.signUp.notRejected') }}!</div>
      <!-- <div class="text-gray-600 mt-1">Please check the fileld form.</div> -->
    </div>
  </div>
  <!-- END: Failed Notification Content -->
  <!-- BEGIN: Success Notification Content -->
  <div id="success-approve-notification-content" class="toastify-content hidden flex">
    <CheckCircleIcon class="text-theme-9" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('superadmin.tools.signUp.approved') }}!</div>
      <!-- <div class="text-gray-600 mt-1">
        Please check your e-mail for further info!
      </div> -->
    </div>
  </div>
  <!-- END: Success Notification Content -->
  <!-- BEGIN: Failed Notification Content -->
  <div id="failed-approve-notification-content" class="toastify-content hidden flex">
    <XCircleIcon class="text-theme-6" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('superadmin.tools.signUp.notApproved') }}!</div>
      <div class="text-gray-600 mt-1">
        {{ null !== errorMessageKey ? $t('error.' + errorMessageKey) : '' }}
      </div>
    </div>
  </div>
  <!-- END: Failed Notification Content -->

  <div id="sign-up-approve-modal" class="modal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <a data-dismiss="modal" href="javascript:;">
          <XIcon class="w-8 h-8 text-gray-500" />
        </a>
        <div class="modal-body p-0">
          <div class="p-5 text-left">
            <div class="text-3xl mt-5">{{ $t('superadmin.tools.signUp.approve') }}</div>
            <div class="text-gray-600 mt-2">
              <div
                class="btn btn-success shadow-md flex items-center mx-2 mt-10"
                @click="approveRequest"
              >
                <CheckIcon class="w-4 h-4 mr-2" /> {{ $t('common.approve') }}
              </div>
              <a
                class="btn btn-default shadow-md flex items-center mx-2 mt-10"
                data-dismiss="modal"
                href="javascript:;"
              >
                {{ $t('common.cancel') }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div id="sign-up-reject-modal" class="modal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <a data-dismiss="modal" href="javascript:;">
          <XIcon class="w-8 h-8 text-gray-500" />
        </a>
        <div class="modal-body p-0">
          <div class="p-5 text-left">
            <div class="text-3xl mt-5">{{ $t('superadmin.tools.signUp.reject') }}</div>
            <div class="text-gray-600 mt-2">
              <div
                class="btn btn-warning shadow-md flex items-center mx-2 mt-10"
                @click="rejectRequest"
              >
                <XIcon class="w-4 h-4 mr-2" /> {{ $t('common.reject') }}
              </div>
              <a
                class="btn btn-default shadow-md flex items-center mx-2 mt-10"
                data-dismiss="modal"
                href="javascript:;"
              >
                {{ $t('common.cancel') }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref } from 'vue'
import Toastify from 'toastify-js'
import superadminApi from '@/services/superadmin/superadmin.api'
import { CheckIcon, XIcon } from '@zhuowenli/vue-feather-icons'

export default defineComponent({
  components: { CheckIcon, XIcon },
  computed: {
    console: () => console
  },
  methods: {
    formatDate(date) {
      const d = new Date(date)
      return (
        d.toISOString().replace(/^(\d+)-(\d+)-(\d+).*$/, '$3.$2.$1') +
        ' ' +
        d.toLocaleTimeString().replace(/^(\d+):(\d+):\d+$/, '$1:$2')
      )
    },
    approveRequest() {
      this.loading = true

      this.hideApproveModal()
      let toastId = ''
      superadminApi
        .approveRequest(this.signUpData.uuid, this.formData)
        .then(
          (response) => {
            this.loading = false
            toastId = 'success-approve-notification-content'
            this.$router.push({ name: 'superadmin-tools-sign-up-list' })
          },
          (error) => {
            this.loading = false
            this.errorMessageKey = error.response.data.error.message_key
            toastId = 'failed-approve-notification-content'
          }
        )
        .then(() => {
          Toastify({
            node: cash('#' + toastId)
              .clone()
              .removeClass('hidden')[0],
            duration: 10000,
            newWindow: true,
            close: true,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true
          }).showToast()
        })
        .then(() => {
          this.errorMessageKey = null
        })
    },
    rejectRequest() {
      this.loading = true
      this.hideRejectModal()
      superadminApi.rejectRequest(this.signUpData.uuid).then(
        (response) => {
          this.loading = false
          Toastify({
            node: cash('#success-reject-notification-content')
              .clone()
              .removeClass('hidden')[0],
            duration: 10000,
            newWindow: true,
            close: true,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true
          }).showToast()
          this.$router.push({ name: 'superadmin-tools-sign-up-list' })
        },
        (error) => {
          console.log(error)
          this.loading = false
          Toastify({
            node: cash('#failed-reject-notification-content')
              .clone()
              .removeClass('hidden')[0],
            duration: 10000,
            newWindow: true,
            close: true,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true
          }).showToast()
        }
      )
    }
  },
  data() {
    return {
      signUpData: {},
      errorMessageKey: null
    }
  },
  mounted() {
    superadminApi.getSignUpDetail(this.$route.params.uuid).then(
      (response) => {
        this.signUpData = response.data
        this.loading = false
      },
      (error) => {
        console.log(error)
        Toastify({
          node: cash('#failed-load-notification-content')
            .clone()
            .removeClass('hidden')[0],
          duration: 10000,
          newWindow: true,
          close: true,
          gravity: 'top',
          position: 'right',
          stopOnFocus: true
        }).showToast()
        this.$router.push({ name: 'superadmin-tools-sign-up-list' })
      }
    )
  },
  setup() {
    const loading = ref(true)

    const showApproveModal = () => {
      cash('#sign-up-approve-modal').modal('show')
    }
    const hideApproveModal = () => {
      cash('#sign-up-approve-modal').modal('hide')
    }

    const showRejectModal = () => {
      cash('#sign-up-reject-modal').modal('show')
    }
    const hideRejectModal = () => {
      cash('#sign-up-reject-modal').modal('hide')
    }

    return {
      loading,
      showApproveModal,
      hideApproveModal,
      showRejectModal,
      hideRejectModal
    }
  }
})
</script>
